'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { computePOWSolutionsWithProgress } from '@/lib/pow';

interface POWWidgetProps {
  endpoint?: string;
  onSolve: (token: string) => void | Promise<void>;
  onError?: (message: string) => void;
}

interface POWChallengeResponse {
  challenge: {
    c: number; // 计算次数
    s: number; // salt长度
    d: number; // 难度
  };
  token: string;
  expires: number;
}

export function POWWidget({ endpoint, onSolve, onError }: POWWidgetProps) {
  const [isComputing, setIsComputing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [challenge, setChallenge] = useState<POWChallengeResponse | null>(null);
  const [error, setError] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [verificationToken, setVerificationToken] = useState<string>('');
  const computationAbortedRef = useRef(false); // 用于标记计算是否被中止
  const retryCountRef = useRef(0); // 重试次数
  const maxRetries = 3; // 最大重试次数
  const verificationTokenRef = useRef<string>(''); // 用于在回调中访问最新的token

  // 获取挑战
  const fetchChallenge = useCallback(async () => {
    // 如果已经有验证token，不再获取
    if (verificationToken) {
      return null;
    }
    
    // 如果正在计算，不再获取
    if (isComputing) {
      return null;
    }
    
    try {
      setError('');
      const apiEndpoint = endpoint || '/api/captcha';
      const baseUrl = apiEndpoint.endsWith('/') ? apiEndpoint.slice(0, -1) : apiEndpoint;
      const url = `${baseUrl}/pow/challenge`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch challenge: ${response.status}`);
      }

      const responseData = await response.json();
      
      // 处理可能的包装格式或直接格式
      let data: POWChallengeResponse;
      if (responseData.data && responseData.data.challenge) {
        data = responseData.data;
      } else if (responseData.challenge) {
        data = responseData;
      } else {
        throw new Error('Invalid challenge response format');
      }
      
      setChallenge(data);
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch challenge';
      setError(message);
      onError?.(message);
      return null;
    }
  }, [endpoint, onError, verificationToken, isComputing]);

  // 兑换解决方案
  const redeemSolutions = useCallback(async (token: string, solutions: number[]) => {
    // 如果已经有验证token，不再兑换
    if (verificationToken) {
      console.log('[POWWidget] 已有验证token，跳过兑换');
      return;
    }

    try {
      const apiEndpoint = endpoint || '/api/captcha';
      const baseUrl = apiEndpoint.endsWith('/') ? apiEndpoint.slice(0, -1) : apiEndpoint;
      const url = `${baseUrl}/pow/redeem`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, solutions }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to redeem solutions');
      }

      const responseData = await response.json();
      
      // 处理可能的包装格式
      let data: { success: boolean; token?: string; expires?: number; message?: string };
      if (responseData.data && typeof responseData.data.success === 'boolean') {
        data = responseData.data;
      } else if (typeof responseData.success === 'boolean') {
        data = responseData;
      } else {
        throw new Error('Invalid redeem response format');
      }
      
      if (data.success && data.token) {
        setVerificationToken(data.token);
        verificationTokenRef.current = data.token; // 同步更新ref
        setIsComputing(false);
        setProgress(100);
        retryCountRef.current = 0; // 成功时重置重试计数
        await onSolve(data.token);
      } else {
        throw new Error(data.message || 'Failed to redeem solutions');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Verification failed';
      setError(errorMsg);
      onError?.(errorMsg);
      setIsComputing(false);
      setProgress(0);
    }
  }, [endpoint, onSolve, onError, verificationToken]);

  // 计算解决方案
  const computeSolutions = useCallback(async () => {
    if (!challenge) {
      console.warn('[POWWidget] 没有挑战数据，无法计算');
      return;
    }

    // 如果已经有验证token，不再计算
    if (verificationToken) {
      console.log('[POWWidget] 已有验证token，跳过计算');
      return;
    }

    // 如果正在计算，不再重复计算
    if (isComputing) {
      console.log('[POWWidget] 正在计算中，跳过重复计算');
      return;
    }

    // 重置中止标志
    computationAbortedRef.current = false;

    try {
      setIsComputing(true);
      setError('');
      setProgress(0);

      // 添加超时保护（根据难度动态调整，最多60秒）
      // 难度越高，需要的时间越长
      const timeoutDuration = challenge.challenge.d > 8 ? 60000 : 30000;
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error('计算超时，请重试'));
        }, timeoutDuration);
      });

      const computationPromise = computePOWSolutionsWithProgress(
        challenge.challenge,
        challenge.token,
        (current, total) => {
          // 检查是否被中止
          if (computationAbortedRef.current) {
            throw new Error('计算已中止');
          }
          setProgress(Math.round((current / total) * 100));
        }
      );

      const computedSolutions = await Promise.race([
        computationPromise,
        timeoutPromise,
      ]);

      // 检查是否在计算过程中被中止
      if (computationAbortedRef.current || verificationToken) {
        console.log('[POWWidget] 计算已中止或被取消');
        setIsComputing(false);
        return;
      }

      // 自动提交解决方案
      await redeemSolutions(challenge.token, computedSolutions);
    } catch (err) {
      // 如果是中止错误，不显示错误信息
      if (computationAbortedRef.current) {
        setIsComputing(false);
        return;
      }
      
      const errorMsg = err instanceof Error ? err.message : 'PoW computation failed';
      console.error('[POWWidget] 计算失败:', errorMsg);
      
      // 如果还有重试机会，自动重试
      if (retryCountRef.current < maxRetries) {
        retryCountRef.current++;
        console.log(`[POWWidget] 重试计算 (${retryCountRef.current}/${maxRetries})`);
        
        // 重置挑战数据，触发重新获取
        setChallenge(null);
        setIsInitialized(false);
        
        // 延迟后重新获取挑战
        setTimeout(async () => {
          // 使用ref检查最新的token状态，避免闭包问题
          if (!verificationTokenRef.current) {
            const newChallenge = await fetchChallenge();
            if (newChallenge && !verificationTokenRef.current) {
              setChallenge(newChallenge);
            }
          }
        }, 1000);
        
        setIsComputing(false);
        setProgress(0);
        return;
      }
      
      // 重试次数用完，显示错误
      setError(errorMsg);
      onError?.(errorMsg);
      setIsComputing(false);
      setProgress(0);
      retryCountRef.current = 0; // 重置重试计数
    }
  }, [challenge, redeemSolutions, onError, verificationToken, isComputing, fetchChallenge]);

  // 同步verificationToken到ref
  useEffect(() => {
    verificationTokenRef.current = verificationToken;
  }, [verificationToken]);

  // 当挑战加载完成后，自动开始计算
  useEffect(() => {
    // 如果已经有验证token，中止任何进行中的计算
    if (verificationToken) {
      computationAbortedRef.current = true;
      setIsComputing(false);
      return;
    }
    
    // 如果没有挑战数据，等待获取
    if (!challenge) {
      return;
    }
    
    // 如果正在计算，不再重复计算
    if (isComputing) {
      return;
    }
    
    // 如果已经初始化，直接计算
    if (isInitialized) {
      computeSolutions();
      return;
    }
    
    // 首次初始化
    setIsInitialized(true);
    
    // 延迟一小段时间让UI更新，然后开始计算
    const timer = setTimeout(() => {
      if (!verificationToken) {
        computeSolutions();
      }
    }, 100);
    
    return () => {
      clearTimeout(timer);
    };
  }, [challenge, verificationToken, isComputing, isInitialized, computeSolutions]);

  // 初始化：获取挑战
  useEffect(() => {
    // 如果已经初始化或有验证token，不再初始化
    if (isInitialized || verificationToken) {
      return;
    }
    
    let mounted = true;
    setIsInitialized(true);

    const init = async () => {
      try {
        const challengeData = await fetchChallenge();
        if (mounted && challengeData && !verificationToken) {
          // 挑战数据已设置，useEffect会自动触发计算
          console.log('[POWWidget] 挑战数据已获取，准备开始计算', {
            difficulty: challengeData.challenge.d,
            count: challengeData.challenge.c,
            saltLength: challengeData.challenge.s
          });
          retryCountRef.current = 0; // 重置重试计数
        } else if (mounted && !challengeData) {
          console.warn('[POWWidget] 获取挑战失败');
          setIsInitialized(false); // 允许重试
        }
      } catch (err) {
        console.error('[POWWidget] 初始化失败:', err);
        if (mounted) {
          setIsInitialized(false); // 允许重试
        }
      }
    };

    init();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, verificationToken]); // 当isInitialized被重置为false时，可以重新初始化

  // 如果已完成，显示成功状态
  if (verificationToken || (progress === 100 && !isComputing)) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span>验证通过</span>
      </div>
    );
  }

  // 如果有错误且未在计算中，显示错误
  if (error && !isComputing) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-500">
        <span>验证失败: {error}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>正在计算验证码... {progress}%</span>
    </div>
  );
}








